import MuiTooltip, { TooltipProps as Mui4TooltipProps } from '@material-ui/core/Tooltip'
import { default as Mui5Tooltip, TooltipProps as Mui5TooltipProps } from '@mui/material/Tooltip'
import { CSSProperties, FC, forwardRef, ReactNode } from 'react'
import { useComponentVersion } from '../hooks'
import { TooltipContent } from '../other/tooltip/TooltipContent'
import { TransitionNone } from './TransitionNone'

type TooltipColor = 'primary' | 'secondary'
type TooltipChildren = Mui5TooltipProps['children']
type TooltipPlacement = Mui5TooltipProps['placement']
type TooltipArrow = Mui5TooltipProps['arrow']

export type TooltipProps = {
  color?: TooltipColor
  children: TooltipChildren
  placement?: TooltipPlacement
  title?: ReactNode
  showTitle?: boolean
  message?: string
  arrow?: TooltipArrow
  linkLabel?: string
  linkUrl?: string
  linkExternal?: boolean
  image?: ReactNode
  fullWidth?: boolean
  disabled?: boolean
  interactive?: boolean // @deprecated
  classes?: Mui4TooltipProps['classes'] | Mui5TooltipProps['classes'] //  @deprecated
  className?: string //  @deprecated
  enterDelay?: number // @deprecated
  enterNextDelay?: number //@deprecated
  enterTouchDelay?: number //@deprecated
  leaveDelay?: number //@deprecated
  leaveTouchDelay?: number //@deprecated
  open?: boolean //@deprecated
  PopperProps?: any //@deprecated
  TransitionComponent?: any //@deprecated
  style?: CSSProperties //@deprecated
  disableHoverListener?: boolean //@deprecated
  disableFocusListener?: boolean //@deprecated
  disableTouchListener?: boolean //@deprecated
  id?: string
  onClose?: (event: React.ChangeEvent<{}>) => void
  onOpen?: (event: React.ChangeEvent<{}>) => void
}

export enum OsTooltipVersion {
  V1 = 1, // OS UI 2.0
  V2 = 2, // MUI5 + Stratis
}

// * MUI Tooltip API Spec  https://mui.com/material-ui/api/tooltip/
export const Tooltip = forwardRef<unknown, TooltipProps>((props, ref) => {
  const {
    color = 'secondary',
    placement = 'top',
    title,
    showTitle = true,
    message,
    arrow = true,
    linkLabel,
    linkUrl,
    linkExternal,
    image,
    children,
    fullWidth,
    classes,
    onClose,
    onOpen,
    interactive,
    ...rest
  } = props

  const version = useComponentVersion('tooltip')

  // Check if there's meaningful content to show
  const hasTitle = title && showTitle && String(title).trim()
  const hasMessage = message && String(message).trim()
  const hasImage = image
  const hasLink = linkLabel && linkUrl
  const hasContent = hasTitle || hasMessage || hasImage || hasLink

  // If no content, just return children without tooltip
  if (!hasContent) {
    void ref
    return <>{children}</>
  }

  switch (version) {
    case OsTooltipVersion.V2:
      return (
        <Mui5Tooltip
          title={
            <TooltipContent
              color={color}
              title={title ?? ''}
              showTitle={showTitle}
              message={message ?? ''}
              linkLabel={linkLabel}
              linkUrl={linkUrl}
              linkExternal={linkExternal}
              image={image}
            />
          }
          color={color}
          arrow={arrow}
          ref={ref}
          placement={placement}
          classes={classes as Mui5TooltipProps['classes']}
          disableInteractive={interactive === false}
          disableTouchListener={false}
          enterTouchDelay={400}
          leaveTouchDelay={2000}
          PopperProps={{
            modifiers: [
              {
                name: 'offset', // margin
                options: {
                  offset: [0, 8],
                },
              },
              {
                name: 'padding', // hover buffer
                options: {
                  padding: { bottom: 10 },
                },
              },
            ],
          }}
          {...rest}
        >
          {children}
        </Mui5Tooltip>
      )

    default:
      void ref
      return (
        <V1Tooltip title={title} color={color} fullWidth={fullWidth} {...rest}>
          {children}
        </V1Tooltip>
      )
  }
})

const V1Tooltip: FC<TooltipProps> = ({
  children,
  title,
  color = 'primary',
  fullWidth,
  interactive,
  classes,
  enterDelay,
  ...rest
}) => {
  if (title === undefined) return <>{children}</>
  return (
    <MuiTooltip
      title={title ?? ''}
      enterTouchDelay={10} // for touch device
      arrow
      TransitionComponent={TransitionNone}
      interactive
      {...rest}
    >
      {children}
    </MuiTooltip>
  )
}

// extend the spec
declare module '@mui/material/Tooltip' {
  interface TooltipOwnProps {
    color: 'primary' | 'secondary'
  }
}
